import clsx from 'clsx';
import React from 'react';
import {Box} from '@mui/material';
import {Icon, Label} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import emailValidator from '@/utils/validators/email';
import {CampaignApprovalStep} from '@/types/campaign';
import {ContentSubmission} from '@/types/contentSubmission';
import {getApprovalStepDisplayName} from '@/views/campaigns/constants/approvalSteps';
import Dropdown, {DropdownOption} from '@/components/dropdown';
import {UserIdentity} from '@/stores/contentSubmissionOffPlatformReviewStore';
import styles from './CommenterIdentityForm.module.scss';

interface CommenterIdentityFormProps {
  values: UserIdentity;
  onChange: (values: UserIdentity) => void;
  contentSubmission: ContentSubmission;
  approvedSteps: Set<string>;
}

function CommenterIdentityForm({
  values,
  onChange,
  contentSubmission,
  approvedSteps,
}: CommenterIdentityFormProps) {
  const approvalSteps = contentSubmission.campaign?.campaignApprovalSteps ?? [];
  const hasApprovalSteps = approvalSteps.length > 0;

  const roleOptions: DropdownOption[] = approvalSteps.map(
    (step: CampaignApprovalStep) => ({
      id: step.step,
      label: approvedSteps.has(step.step)
        ? translate('already-approved-role', {
            role: getApprovalStepDisplayName(step.step),
          })
        : getApprovalStepDisplayName(step.step),
    })
  );

  const selectedRoleLabel = values.role
    ? getApprovalStepDisplayName(values.role)
    : translate('Select your role');

  return (
    <>
      <Box className={styles.identityField}>
        <Label size="sm" className={styles.identityLabel}>
          {translate('Name')}
        </Label>
        <input
          className={styles.identityInput}
          type="text"
          placeholder={translate('Your name')}
          value={values.name}
          onChange={(e) => onChange({...values, name: e.target.value})}
          data-testid="identity-form-name"
        />
      </Box>
      <Box className={styles.identityField}>
        <Label size="sm" className={styles.identityLabel}>
          {translate('Email')}
        </Label>
        <Box className={styles.identityInputWrapper}>
          <input
            className={clsx(
              styles.identityInput,
              values.email && styles.withValidationIcon
            )}
            type="email"
            placeholder={translate('your@email.com')}
            value={values.email}
            onChange={(e) => onChange({...values, email: e.target.value})}
            data-testid="identity-form-email"
          />
          {values.email ? (
            <span
              className={clsx(
                styles.identityValidationIcon,
                emailValidator(values.email) ? styles.valid : styles.invalid
              )}
            >
              <Icon
                name={
                  emailValidator(values.email)
                    ? 'Actions-Patch-Accept-Line'
                    : 'Actions-Close-Small'
                }
                size="small"
                appearance="neutral"
              />
            </span>
          ) : null}
        </Box>
      </Box>
      {hasApprovalSteps ? (
        <Box className={styles.identityField}>
          <Label size="sm" className={styles.identityLabel}>
            {translate('Role')}
          </Label>
          <Dropdown
            label={selectedRoleLabel}
            options={roleOptions}
            selectedIds={values.role ? [values.role] : []}
            onChange={(option) => onChange({...values, role: option.id})}
            selectionMode="single"
            matchTriggerWidth
            className={styles.roleDropdown}
          />
        </Box>
      ) : null}
    </>
  );
}

export default CommenterIdentityForm;
